import { useCallback, useMemo, useState } from 'react';
import { useShipTrackFetch } from '@ais-ui/api';

import ShipTrackMap from './ShipTrackMap';
import ShipTrackChart from './ShipTrackChart';
import { LoadingSpinner } from '@ais-ui/ui-core';

interface BidirectionalMapChartProps {
  selectedValue: 'sog' | 'cog';
  imo: number;
}

export default function BidirectionalMapChart({
  selectedValue,
  imo,
}: BidirectionalMapChartProps) {
  const [hoveredPointIndex, setHoveredPointIndex] = useState<number | null>(
    null
  );

  const shipData = useMemo(() => {
    return { imo: imo };
  }, [imo]);

  // Set time range for data fetch - last 24 hours (stable dates)
  const { beginDate, endDate } = useMemo(() => {
    const end = new Date();
    const begin = new Date();
    begin.setHours(begin.getHours() - 24);
    return {
      beginDate: begin,
      endDate: end,
    };
  }, []); // Empty dependency array ensures dates are only calculated once
  const resolution = '00:10:00';

  const { data, isLoading, isError, parsedError, isNoDataError } =
    useShipTrackFetch({
      shipData,
      beginDate,
      endDate,
      resolution,
    });

  // Handle error message in component (UI concern)
  const getErrorMessage = useCallback(() => {
    if (!parsedError) return 'Error loading ship data';
    return parsedError.message;
  }, [parsedError]);

  // Handle error styling in component (UI concern)
  const getErrorClassName = useCallback(() => {
    if (isNoDataError) {
      return 'text-muted-foreground'; // Neutral color for no data available
    }
    return 'text-destructive'; // Red color for actual errors
  }, [isNoDataError]);

  // Handle hover synchronization between map and chart
  const handlePointHover = useCallback((index: number | null) => {
    setHoveredPointIndex(index);
  }, []);

  const shipTrack = useMemo(() => {
    if (
      !data ||
      !data.track ||
      !data.track.coordinates ||
      data.track.coordinates.length === 0
    ) {
      return null;
    }

    // Create a deep copy of the data to avoid mutating the original
    const roundedTrack = {
      ...data,
      track: {
        ...data.track,
        coordinates: data.track.coordinates.map((point) => {
          if (Array.isArray(point) && point.length >= 5) {
            // Create a new array to avoid mutating the original
            const newPoint = [...point];

            // Round SOG (speed) to 1 decimal place (index 4)
            newPoint[4] = Math.round(point[4] * 10) / 10;

            // Round COG (course) to 1 decimal place (index 3)
            newPoint[3] = Math.round(point[3] * 10) / 10;

            return newPoint;
          }
          return point;
        }),
      },
    };

    return roundedTrack;
  }, [data]);

  // If loading or error, show appropriate message
  if (isLoading) {
    return (
      <div className="w-full p-6 rounded-lg">
        <div className="h-[400px] w-full flex items-center justify-center">
          <LoadingSpinner></LoadingSpinner>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="w-full p-6 rounded-lg">
        <div
          className={`h-[400px] w-full flex items-center justify-center ${getErrorClassName()}`}
        >
          {getErrorMessage()}
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 grid-rows-2 gap-4">
      <ShipTrackChart
        trackData={shipTrack}
        hoveredPointIndex={hoveredPointIndex}
        onPointHover={handlePointHover}
        className="row-end-2 col-span-3"
        dataType={selectedValue}
      />
      <ShipTrackMap
        trackData={shipTrack}
        hoveredPointIndex={hoveredPointIndex}
        onPointHover={handlePointHover}
        className="row-start-2 row-end-3 col-span-3"
        dataType={selectedValue}
      />
    </div>
  );
}
